import { Box, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from '../../../../redux/store';
import { CollapseSidebar } from '../../../../reusable-components/collapse-sidebar';
import { createColumns } from '../../../../reusable-components/datagrid/custom-filters';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { SHOP_CATALOG } from '../../../../reusable-components/datagrid/shopColumns';
import { ScopeGuard, useScopeCheck } from '../../../../reusable-components/scopes';
import { CategoryNavbar } from './order-navbar';
import QuantityField from './QuantityField';
import BudgetBox from './BudgetBox';
import ItemsGrid from './ItemsGrid';
import CategoryViewHeader from './CategoryViewHeader';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';
import { ShopCartContext, ShopDataContext } from './_ShopViewContext';

ShopView.propTypes = {
  isFetchOrderSheetLoading: PropTypes.bool,
};

export default function ShopView({ isFetchOrderSheetLoading }) {
  const hasBudgetViewAccess = useScopeCheck(['View-Order'], true);
  const isCartCheckout = useScopeCheck(['Cart-Check'], true);

  const [searchParams] = useSearchParams();
  const { facilityId = '' } = useParams();

  const categoryId = searchParams.get('categoryId') || null;

  const [sortModel, setSortModel] = useState([
    { field: 'quantity', sort: 'desc' },
    { field: 'previouslyOrderedItems', sort: 'desc' },
  ]);
  const [openSidebar, setOpenSidebar] = useState(true);
  const [initialSort, setInitialSort] = useState(true);
  const { data: { items } } = useSelector((state) => state.orderSheet);
  const { data, setData } = useContext(ShopDataContext);

  const isLoading = useSelector((state) => state.orderSheet.isLoading);

  useEffect(() => {
    setData(items.map((item) => ({ ...item, quantity: null })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (data.length > 0 && initialSort) {
      setSortModel([
        { field: 'quantity', sort: 'desc' },
        { field: 'previouslyOrderedItems', sort: 'desc' },
      ]);
      setInitialSort(false);
    }
  }, [data.length, initialSort]);

  const customToolbar = useCallback(
    () => <CategoryNavbar />,
    []
  );

  const quantity = {
    field: 'quantity',
    headerName: 'Qty',
    flex: 0.5,
    minWidth: 80,
    renderCell: (params) =>
      params.row?.invalidItem ? (
        params.row.invalidItemQuantity ?? 0
      ) : (
        <QuantityField
          row={params.row}
          callback={(val) => {
            params.row.quantity = val;
          }}
        />
      ),
    valueGetter: (params) => (params.row?.invalidItem ? 9999 : params.row?.quantity ?? 0),
  };

  const vendor = {
    field: 'vendor',
    headerName: 'Vendor',
    flex: 0.8,
    minWidth: 90,
    renderCell: (params) => (
      <Box
        component="div"
        sx={{
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {params.row.productDetails[0].vendor}
      </Box>
    ),
    type: 'customSelect',
    getFilterValues: (row) => [row.productDetails[0].vendor].filter((v) => v),
    valueGetter: (params) => params.row.productDetails[0].vendor,
  };

  const mergeColumns = [...SHOP_CATALOG, quantity];

  if (isCartCheckout) {
    mergeColumns.push(vendor);
  }

  const [view, setView] = useState(localStorage.getItem('orders-new-order-shop-view-list-style') ?? 'list');
  const columns = createColumns(mergeColumns);

  useEffect(() => {
    if (view !== localStorage.getItem('orders-new-order-shop-view-list-style'))
      localStorage.setItem('orders-new-order-shop-view-list-style', view);
  }, [view]);

  return (
    <>
      <CategoryViewHeader view={view} setView={setView} />
      <Grid
        item
        xs={12}
        sx={{
          p: 2,
        }}
      >
        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
          <Grid item xs={hasBudgetViewAccess ? (openSidebar ? 9.3 : 11.9) : 12}>
            <Grid item xs={12}>
              {view === 'grid' && !isFetchOrderSheetLoading ? (
                <ItemsGrid />
              ) : view === 'grid' && isFetchOrderSheetLoading ? (
                <Box
                  sx={{
                    width: '100%',
                    height: 'calc(100vh - 250px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <LoadingAnimation />
                </Box>
              ) : (
                <CustomDataGrid
                  gridId="orders-new-order-shop-view"
                  sx={{ height: 'calc(100vh - 250px)' }}
                  data={data}
                  gridColumns={columns}
                  sort={sortModel}
                  CustomLeftToolbar={customToolbar}
                  getRowId={(row) => row.id}
                  getRowClassName={(params) => (params.row?.invalidItem ? `disabled` : '')}
                  disableSelectionOnClick
                  isLoading={isLoading || isFetchOrderSheetLoading}
                  key={`${facilityId}-${categoryId}`}
                />
              )}
            </Grid>
          </Grid>
          {hasBudgetViewAccess && (
            <Grid item xs={openSidebar ? 2.7 : 0.1} style={{ alignSelf: 'stretch', position: 'relative' }}>
              <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                <CollapseSidebar openSidebar={openSidebar} setOpenSidebar={setOpenSidebar}>
                  {openSidebar && (
                    <>
                      <ScopeGuard scopes={['View-Order']} allowAdmin>
                        <Box>
                          <Grid item xs={12}>
                            <Grid item>
                              <CartBudgetBox catId={parseInt(categoryId, 10)} facId={facilityId} />
                            </Grid>
                          </Grid>
                        </Box>
                      </ScopeGuard>
                    </>
                  )}
                </CollapseSidebar>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
}

function CartBudgetBox({ catId, facId }) {
  const { cart: cartData } = useContext(ShopCartContext);
  const { data } = useContext(ShopDataContext);

  const budgetItems = data.filter(x => x.productDetails?.length && (x.quantity > 0 || cartData[x.id] > 0)).map(x => ({
    quantity: cartData[x.id] !== undefined ? cartData[x.id] : x.quantity,
    agoraCategoryId: x.displayCategory?.id,
    agoraSubcategoryId: x.displaySubcategory?.id,
    price: x.productDetails[0].price,
  }));

  const categoryIds = [...new Set([catId, ...budgetItems.map(item => item.agoraCategoryId)])];

  return categoryIds.map(categoryId => (
    <Box key={categoryId} sx={{ my: 1 }}>
      <BudgetBox
        cart={budgetItems.filter(item => item.agoraCategoryId === categoryId)}
        catId={categoryId}
        facId={facId}
      />
    </Box>
  ));
}
